<template>
  <v-card scrollable>
    <v-row class="pa-5 ma-0">
      <v-col class="flex-grow-0 pa-0 ma-0">
        <div class="image-upload-ctn pr-5">
          <div class="d-flex flex-row align-center mb-3 img-ctn" v-if="productImage">
            <img :src="productImage">
            <v-btn v-if="productForm.image" class="delete-btn" icon @click="removeProductPicture()">
              <v-icon color="red">mdi-minus-circle-outline</v-icon>
            </v-btn>
          </div>
          <div class="font-weight-light hint pt-1 mb-2">
            {{$t('product_supported_format')}}, {{$t('product_file_size')}}
          </div>
          <ImageCropper input-key="logo" @image-selected="onImageSelect" :show-select-dialog="true" :max-file-megabytes="2" accept="image/png, image/jpeg, image/jpg"></ImageCropper>
        </div>
      </v-col>
      <v-col class="py-0">
        <v-form v-model="valid" ref="product-form">
          <v-row>
            <v-col :cols="product.id ? 6 : 12" class="pt-0">
              <v-text-field v-model="productForm.name" name="name" persistent-hint :label="$t('product_name')" :hint="$t('product_name_hint')" @focus="clearErrors('name')" :rules="rules.name" :error-messages="errorMessages.name" required></v-text-field>
            </v-col>
            <v-col :cols="product.id ? 6 : 12" class="pt-0">
              <v-text-field v-model="productForm.sku" name="sku" persistent-hint :label="$t('product_sku')" :hint="$t('product_sku_hint')" :rules="rules.sku" @focus="clearErrors('sku')" :error-messages="errorMessages.sku" required class="sku-input"></v-text-field>
            </v-col>
            <v-col cols="11" class="pt-0">
              <v-text-field ref="product-url" validate-on-blur name="url" @blur="onBlurUrl" @focus="onFocusUrl" v-model="productForm.client_url" persistent-hint :rules="rules.client_url" :hint="$t('product_url_hint')" :label="$t('product_url')"></v-text-field>
            </v-col>
            <v-col cols="12" class="pt-0">
              <v-autocomplete v-model="productForm.brand"
                :items="brands"
                item-text="name"
                item-value="id"
                :label="$t('brand')"
                :rules="rules.brand"
              >
              <template v-slot:selection="{ item }">
                <span>
                  [{{ item.reference }}] {{ item.name }}
                </span>
              </template>

              <template v-slot:item="{ item }">
                <span>
                  [{{ item.reference }}] {{ item.name }}
                </span>
              </template>
              </v-autocomplete>
            </v-col>

            <v-col v-if="isAvailable('qrmanager:product:description') && product.id" cols="12" class="pt-0">
              <v-textarea v-model="productForm.description" counter="500" :label="$t('description')" persistent-hint :hint="$t('description_hint')" :rules="rules.description"></v-textarea>
            </v-col>
          </v-row>
        </v-form>
      </v-col>
    </v-row>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn text @click="handleClose()">{{$t('close')}}</v-btn>
      <v-btn text color="primary" @click="handleSave()" :loading="savingProduct" >{{$t('save')}}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>

import ImageCropper from '@/components/utils/ImageCropper'
import { mapGetters, mapMutations } from 'vuex'
import amplitude from 'amplitude-js'

export default {
  name: 'ProductForm',
  props: ['product', 'campaign', 'productId'],
  data () {
    return {
      valid: false,
      savingProduct: false,
      isFromGallery: false,
      galleryImageKey: null,
      errorMessages: {
        name: [],
        sku: []
      },
      productForm: {
        name: '',
        sku: '',
        client_url: '',
        description: ''
      },
      rules: {
        name: [
          v => !!v || this.$t('field_required')
        ],
        sku: [
          v => !!v || this.$t('field_required')
        ],
        brand: [
          v => !!v || this.$t('field_required')
        ],
        client_url: [
          v => (/^(?:(?:(?:https?|ftp):\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,}))\.?)(?::\d{2,5})?(?:[/?#]\S*)?|mailto:[^\s@]+@[^\s@]+\.[^\s]+|tel:\+?(?=(?:\D*\d){3,15}$)[0-9().-]+)$/i.test(v) || !v || !v.length) || this.$t('product_url_invalid')
        ],
        description: [
          v => (v || '').length <= 200 || this.$t('maximum_characters_limit', { count: 200 })
        ]
      }
    }
  },
  components: {
    ImageCropper
  },
  computed: {
    ...mapGetters(['campaigns', 'isAvailable', 'filteredProductCount', 'defaultBrand', 'selectedBrand', 'brands']),

    isDuplicatingProduct () {
      return this.$route.name === 'products.duplicate'
    },

    productImage () {
      return this.productForm.image ? this.productForm.image : this.productForm.brand && this.productForm.brand.image ? this.productForm.brand.image : require('@/assets/product-placeholder.png')
    }
  },
  watch: {
    product (newVal) {
      if (newVal && newVal.sku !== this.productForm.sku) {
        if (this.isDuplicatingProduct) {
          this.productForm = {
            name: newVal.name + '_copy',
            sku: newVal.sku + '_copy',
            client_url: newVal.client_url,
            description: newVal.description,
            campaign: newVal.campaign?.id || this.campaign,
            brand: newVal.brand?.id || this.brand?.id
          }
        } else {
          this.productForm = {
            ...newVal
          }
        }
      }
    },

    'productForm.client_url' (newVal) {
      if (newVal) {
        const tmp = newVal.substr(8)
        if (tmp && tmp.indexOf('https://') === 0) {
          this.productForm.client_url = tmp
        }
      }
    },

    productId (newVal) {
      if (!newVal) {
        this.productForm = {
          name: '',
          sku: '',
          client_url: '',
          description: ''
        }
      }
    }
  },
  methods: {
    ...mapMutations({
      setProductUpdateStatus: 'UPDATE_PRODUCT_UPDATE_STATUS',
      setSelectedBrand: 'SET_SELECTED_BRAND'
    }),

    onFocusUrl () {
      if (!this.productForm.client_url) {
        this.productForm.client_url = 'https://'
      }
    },

    onBlurUrl () {
      if (this.productForm.client_url && this.productForm.client_url === 'https://') {
        this.productForm.client_url = ''
      }
    },

    clearErrors (key) {
      if (key) {
        this.errorMessages[key] = []
      } else {
        this.errorMessages = {
          name: [],
          sku: []
        }
      }
    },

    removeProductPicture () {
      this.productForm.image = null
    },

    handleClose (product) {
      this.$refs['product-form'].reset()
      this.savingProduct = false
      this.productForm.image = null
      this.$emit('on-close-dialog', product)
      this.isFromGallery = false
      this.galleryImageKey = null
    },

    async handleSave () {
      this.$refs['product-form'].validate()
      console.log('is form valid', this.valid)
      if (!this.valid) {
        return
      }
      this.savingProduct = true

      if (this.isDuplicatingProduct) {
        try {
          this.setFormGalleryImage()
          const newProduct = await this.$store.dispatch('createProduct', { product: this.productForm })

          if (this.product?.elabel?.has_data) {
            const elabelData = await this.$store.dispatch('loadElabel', { id: this.product.id, sku: this.product.sku })
            if (elabelData) {
              const newElabel = await this.$store.dispatch('updateElabel', {
                id: newProduct.id,
                name: newProduct.sku,
                data: elabelData.data,
                published: elabelData.is_published || false
              })
              newProduct.elabel = {
                id: newElabel.id,
                name: newElabel.name,
                is_published: newElabel.is_published,
                has_data: true
              }
            }
          }

          amplitude.getInstance().logEvent('product duplicated')
          this.setProductUpdateStatus(true)
          this.handleClose()
          if (this.campaigns.length > 1) {
            this.$emit('switch-to-product-brand', newProduct)
          }
        } catch (err) {
          for (const key in err.data) {
            this.errorMessages[key] = err.data[key]
          }
          this.savingProduct = false
        }
      } else if (this.productForm.id) {
        // Updating product
        try {
          this.setFormGalleryImage()
          const product = await this.$store.dispatch('updateProduct', { product: this.productForm })
          if (!this.isFromGallery && !this.productForm.image) {
            await this.$store.dispatch('deleteProductPicture', { product: product })
          }
          amplitude.getInstance().logEvent('product save')
          this.setProductUpdateStatus(true)
          this.handleClose(product)
          if (this.campaigns.length > 1) {
            this.$emit('switch-to-product-brand', product)
          }
        } catch (err) {
          for (const key in err.data) {
            this.errorMessages[key] = err.data[key]
          }
          this.savingProduct = false
        }
      } else {
        // Creating new product
        this.setFormGalleryImage()
        if (!this.productForm.campaign) {
          this.productForm.campaign = this.campaign
        }
        try {
          if (this.defaultBrand && !this.productForm.brand) {
            this.productForm.brand = this.defaultBrand.reference
          }
          const product = await this.$store.dispatch('createProduct', { product: this.productForm })
          amplitude.getInstance().logEvent('product create')
          this.setProductUpdateStatus(true)
          this.handleClose()
          this.$emit('on-product-created', product)
        } catch (err) {
          for (const key in err.data) {
            this.errorMessages[key] = err.data[key]
          }
          this.savingProduct = false
        }
      }
    },

    onImageSelect (image, fromGallery = false) {
      if (fromGallery) {
        this.isFromGallery = fromGallery
        const selectedImage = image.file
        this.galleryImageKey = image.key
        this.productForm = Object.assign({}, this.productForm, { image: selectedImage })
      } else {
        this.productForm = Object.assign({}, this.productForm, { image: image })
      }
    },

    setFormGalleryImage () {
      if (this.isFromGallery && this.galleryImageKey) {
        this.productForm.gallery_image = this.galleryImageKey
      }
    }
  },
  created () {
    if (this.isDuplicatingProduct && this.product && this.product.id) {
      this.productForm = {
        name: this.product.name + '_copy',
        sku: this.product.sku + '_copy',
        client_url: this.product.client_url,
        description: this.product.description,
        campaign: this.product.campaign?.id || this.campaign,
        brand: this.product.brand?.id || this.brand?.id
        // image isn't copied, since it's in url (not blob) format
      }
    } else if (this.product && this.product.id) {
      this.productForm = {
        ...this.product
      }
      this.productForm.brand = this.productForm.brand.id
    } else {
      if (this.campaigns.length === 1 && this.selectedBrand?.id) {
        this.productForm.brand = this.selectedBrand.id
      }
      this.productForm.campaign = this.campaign
    }
  }
}

</script>

<style lang="sass" scoped>

.image-upload-ctn
  text-align: center
  img
    max-width: 200px
    max-height: 300px
    margin-left: auto
    margin-right: auto

.hint
  font-size: 10px
  text-align: center
  margin-right: 20px

.img-ctn
  position: relative
  .delete-btn
    position: absolute
    top: 10px
    right: 10px

.sku-input::v-deep .v-messages__message
  line-height: 18px

</style>
